import { computed, inject, Injectable, signal } from '@angular/core'
import { Observable, tap, map } from 'rxjs'
import { Publisher } from '@models/publisher.model'
import { PublisherApiService } from './publisher-api.service'

@Injectable({
  providedIn: 'root',
})
export class PublisherHelperService {
  private publisherApiService = inject(PublisherApiService)

  private allPublishers = signal<Publisher[]>([])
  private selectedPublisher = signal<Publisher | null>(null)
  private selectedPublishers = signal<Publisher[]>([])
  private tempSelectedPublishers = signal<Publisher[]>([])

  getAllPublishers = this.allPublishers.asReadonly()
  getSelectedPublisher = this.selectedPublisher.asReadonly()
  getSelectedPublishers = this.selectedPublishers.asReadonly()
  getTempSelectedPublishers = this.tempSelectedPublishers.asReadonly()

  hasSelectionChanged = computed(() => {
    const selected = this.selectedPublishers()
    const temp = this.tempSelectedPublishers()

    if (selected.length !== temp.length) return true

    const selectedIds = new Set(selected.map(p => p.id))
    const tempIds = new Set(temp.map(p => p.id))

    for (const id of selectedIds) {
      if (!tempIds.has(id)) return true
    }
    for (const id of tempIds) {
      if (!selectedIds.has(id)) return true
    }

    return false
  })

  loadAllPublishers(): Observable<Publisher[]> {
    return this.publisherApiService.getAllPublishers$().pipe(
      tap({
        next: (response) => {
          this.setAllPublishers(response.publishers)
        },
        error: (error) => {
          console.error('Failed to load publishers from API, using mock data:', error)
        },
      }),
      map(response => response.publishers)
    )
  }

  selectPublisher(publisher: Publisher | null): void {
    this.selectedPublisher.set(publisher)
    if (publisher) {
      this.selectedPublishers.set([publisher])
    } else {
      this.selectedPublishers.set([])
    }
  }

  togglePublisherSelection(publisher: Publisher): void {
    const current = this.tempSelectedPublishers()
    const index = current.findIndex(p => p.id === publisher.id)

    if (index > -1) {
      this.tempSelectedPublishers.set(current.filter(p => p.id !== publisher.id))
    } else {
      this.tempSelectedPublishers.set([...current, publisher])
    }
  }

  applyPublisherSelection(): void {
    const temp = this.tempSelectedPublishers()
    this.selectedPublishers.set(temp)

    if (temp.length === 1) {
      this.selectedPublisher.set(temp[0])
    } else if (temp.length === 0) {
      this.selectedPublisher.set(null)
    } else {
      this.selectedPublisher.set(temp[0])
    }

    this.saveSelectedPublishersToLocalStorage(temp)
  }

  cancelPublisherSelection(): void {
    this.tempSelectedPublishers.set([...this.selectedPublishers()])
  }

  initTempSelection(): void {
    this.tempSelectedPublishers.set([...this.selectedPublishers()])
  }

  isPublisherSelected(publisherId: string): boolean {
    return this.selectedPublishers().some(p => p.id === publisherId)
  }

  isTempPublisherSelected(publisherId: string): boolean {
    return this.tempSelectedPublishers().some(p => p.id === publisherId)
  }

  clearSelection(): void {
    this.selectedPublisher.set(null)
    this.selectedPublishers.set([])
    this.tempSelectedPublishers.set([])
    localStorage.removeItem('selectedPublisherIds')
  }

  resetToDefaultSelection(): void {
    this.selectedPublisher.set(null)
    const defaultSelected = this.allPublishers().filter(p => p.name !== 'Stock Content')
    this.selectedPublishers.set(defaultSelected)
    this.tempSelectedPublishers.set(defaultSelected)
    this.saveSelectedPublishersToLocalStorage(defaultSelected)
  }

  setAllPublishers(publishers: Publisher[]): void {
    const allPublishers = [...publishers]
    this.allPublishers.set(allPublishers)

    const savedSelection = this.loadSelectedPublishersFromLocalStorage()

    if (savedSelection && savedSelection.length > 0) {
      const validSelectedPublishers = savedSelection
        .map(id => allPublishers.find(p => p.id === id))
        .filter((p): p is Publisher => p !== undefined)

      if (validSelectedPublishers.length > 0) {
        this.selectedPublishers.set(validSelectedPublishers)
        this.tempSelectedPublishers.set(validSelectedPublishers)

        if (validSelectedPublishers.length === 1) {
          this.selectedPublisher.set(validSelectedPublishers[0])
        } else {
          this.selectedPublisher.set(validSelectedPublishers[0])
        }
        return
      }
    }

    const defaultSelected = allPublishers.filter(p => p.name !== 'Stock Content')
    this.selectedPublishers.set(defaultSelected)
    this.tempSelectedPublishers.set(defaultSelected)
  }

  private saveSelectedPublishersToLocalStorage(publishers: Publisher[]): void {
    const publisherIds = publishers.map(p => p.id)
    localStorage.setItem('selectedPublisherIds', JSON.stringify(publisherIds))
  }

  private loadSelectedPublishersFromLocalStorage(): string[] | null {
    const saved = localStorage.getItem('selectedPublisherIds')
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        return null
      }
    }
    return null
  }
}
